import {Component, forwardRef, HostBinding, Input} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BaseFilePickerAdapter} from "../../shared/BaseFilePickerAdapter";
import {APIService} from "../../shared/api.service";
import {NotifierService} from "../../shared/notifier.service";
import {FileEntry} from "../../interfaces/fileEntry";
import {FilePickerAdapter, UploadResponse, UploadStatus} from "../../interfaces/filePickerAdapter";
import { HttpErrorResponse } from "@angular/common/http";

@Component({
    selector: 'bst-uploader',
    templateUrl: './uploader.component.html',
    styleUrls: ['./uploader.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UploaderComponent),
            multi: true
        }
    ],
    standalone: false
})
export class UploaderComponent extends SubscribedComponent implements ControlValueAccessor{

  @Input() public path: string = null;
  @Input() public disabled: boolean = false;
  @Input() public data: {[key:string]: any} = null;
  @Input() public accepts: string[] = null;
  @Input() public size: number = Infinity; // In Mb
  @Input() public value: FileEntry = null;

  @HostBinding('style.opacity')
  get opacity() {
    return this.disabled ? 0.25 : 1;
  }

  // Function to call when the value changes.
  onChange = (value: FileEntry) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  public progress: number = 0;

  public adapter: FilePickerAdapter;

  constructor(protected api: APIService, protected notifier: NotifierService) {
    super();
  }

  public get accept(){
    return this.accepts ? this.accepts.join(',') : '*';
  }

  public get name(){
    return this.value ? this.value.name : null;
  }

  public onFileDownload(event) {
    if(this.value && !this.adapter.isLoading){
      const b = this.adapter.downloadFile(this.value).subscribe(async (value: UploadResponse) => {
        if(value.status === UploadStatus.DOWNLOADED) {
          await this.notifier.notify(this.value.name, 'uploaddownload', 'primary', 'file-outline');
        }
        b.unsubscribe();
      });
    }
  }

  public pick(element: HTMLInputElement){
    if(!this.adapter.isLoading){
      element.click();
    }
  }

  public async onFileClear(event){
    if(this.value && !this.adapter.isLoading){
      if(await this.notifier.confirm('sure?', 'This will remove the file from the system.')) {
        const b = this.adapter.removeFile(this.value).subscribe(async (value: UploadResponse) => {
          if (value) {
            if (value.status === UploadStatus.DELETED) {
              await this.notifier.notify((<FileEntry>value.body).name, 'uploaddelete', 'success', 'file-remove-outline');
              this.writeValue(null);
            } else {
              await this.notifier.notify('uploaddeleteerror', null, 'danger');
            }
            b.unsubscribe();
          }
        });
      }
    }
  }

  public onFileSelected(event) {
    const file:File = event.target.files[0];

    if (file && !this.adapter.isLoading) {

      // Where we will do file validation
      if(file.size / 1024 > this.size * 1024 * 1024){
        event.target.value = null;
        return this.notifier.notify('toobig', 'uploaderror', 'warning', 'alert-triangle-outline', 4000, {
          value: Math.floor(this.size)
        });
      }
      else if(this.accepts && this.accepts.indexOf(file.type) === -1){
        event.target.value = null;
        return this.notifier.notify('wrongtype', 'uploaderror', 'warning', 'alert-triangle-outline', 4000, {
          value: this.accepts.join(', ')
        });
      }

      const b = this.adapter.uploadFile(file, this.data).subscribe(async (value: UploadResponse) => {

        if(value) {
          switch (value.status) {

            case UploadStatus.IN_PROGRESS:
              this.progress = value.progress;

              if (this.value) {
                this.writeValue(null);
              }
              break;

            case UploadStatus.UPLOADED:
              this.progress = 100;
              this.writeValue(<FileEntry>value.body);
              await this.notifier.notify((<FileEntry>value.body).name, 'uploaded', 'success', 'file-add-outline');
              event.target.value = null;
              b.unsubscribe();
              break;

            case UploadStatus.ERROR:
              await this.notifier.notify((<HttpErrorResponse>value.body).statusText, 'uploaderror', 'warning', 'alert-triangle-outline');
              console.error(value.body);
              event.target.value = null;
              this.progress = 100;
              this.writeValue(null);
              b.unsubscribe();
              break;
          }
        }
      });
    }
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.adapter = new BaseFilePickerAdapter(this.api, this.notifier, this.path);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: FileEntry): void {
    this.value = value;
    this.onChange(value);
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
